import { Permission } from '@interfaces/a-organizar/permission';
import { PermissionGroup } from '@interfaces/a-organizar/permissionGroup';
import { classifyAction } from '@app/features/dashboard/group-register/permission-tree/permission-action.utils';
import { roleLabel } from '@app/features/dashboard/group-register/permission-tree/permission-labels';
import { buildPermissionTree } from '@app/features/dashboard/group-register/permission-tree/permission-tree.builder';
import { PermissionModuleNode } from '@app/features/dashboard/group-register/permission-tree/permission-tree.models';

/**
 * Regras puras da lista de grupos de permissão (card 3902): rótulo e cor do tipo de usuário, o que cada grupo libera
 * (módulos do menu, leitura x gravação), filtro por tipo e busca. Detalhe no README da pasta.
 */

/**
 * O que a confirmação de exclusão diz sobre os usuários do grupo. `null` é contagem indisponível (backend sem
 * `usersCount`): nunca vira "nenhum usuário", para a confirmação não afirmar o que não sabe.
 */
export function describeRemovalImpact(usersCount: number | null): string {
  if (usersCount === null) {
    return 'Não foi possível contar os usuários deste grupo: quem estiver nele perde as permissões que só ele dava.';
  }
  if (usersCount === 0) return 'Nenhum usuário está neste grupo.';
  if (usersCount === 1) return 'O usuário deste grupo perde as permissões que só ele dava.';
  return `Os ${usersCount} usuários deste grupo perdem as permissões que só ele dava.`;
}

/** Tom do selo do tipo de usuário. */
export type RoleTone = 'info' | 'brand' | 'navy' | 'danger' | 'neutral';

interface RoleStyle {
  /** Rótulo curto do chip e do selo (a tela de edição usa o longo, "Parceiro comercial"). */
  label: string;
  tone: RoleTone;
  /** Ordem dos chips: do tipo mais comum para o mais raro. */
  order: number;
}

const ROLE_STYLES: Record<string, RoleStyle> = {
  COMPANY: { label: 'Empresa', tone: 'info', order: 1 },
  PARTNER: { label: 'Parceiro', tone: 'brand', order: 2 },
  TENANT: { label: 'Equipe interna', tone: 'navy', order: 3 },
  ADMIN: { label: 'Administrador', tone: 'danger', order: 4 },
  CUSTOMER: { label: 'Cliente', tone: 'neutral', order: 5 },
};

/** Papel sem entrada no mapa (papel novo): rótulo da tela de edição, selo cinza, depois dos conhecidos. */
export function roleStyle(roleName: string | null | undefined): RoleStyle {
  const key = (roleName ?? '').trim().toUpperCase();
  return (
    ROLE_STYLES[key] ?? {
      label: roleLabel(roleName ?? '') || 'Sem tipo',
      tone: 'neutral',
      order: 99,
    }
  );
}

/** Uma linha da lista, já com tudo que a tela mostra. */
export interface GroupListRow {
  group: PermissionGroup;
  roleId: string;
  roleLabel: string;
  roleTone: RoleTone;
  /** "Controle · Empréstimos, Derivativos e Gestão de riscos". Vazio quando o catálogo do papel não carregou. */
  modulesText: string;
  total: number;
  read: number;
  write: number;
  /** Larguras da barra (0 a 100). */
  readPercent: number;
  writePercent: number;
  /** "40 leitura · nenhuma gravação". */
  breakdown: string;
  usersCount: number | null;
  isDefault: boolean;
  /** Nome sem acento e em minúsculas, para a busca. */
  searchKey: string;
}

/** Chip de filtro por tipo de usuário. `roleId` vazio = "Todos". */
export interface RoleChip {
  roleId: string;
  label: string;
  count: number;
}

/** Texto sem acento, em minúsculas e sem espaço nas pontas. */
export function normalizeText(text: string | null | undefined): string {
  return (text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/**
 * Índice de uma claim no catálogo do papel: leitura ou gravação (menu conta como leitura: só mostra o item) e o
 * módulo e o item (área ou tela solta no módulo) onde ela mora no menu.
 */
export interface CatalogEntry {
  write: boolean;
  /** `null` no módulo "Outras (fora do menu)". */
  moduleId: string | null;
  moduleLabel: string;
  itemLabel: string;
}

export interface RoleCatalogIndex {
  entries: Map<string, CatalogEntry>;
  /** Módulos do menu que o papel tem (sem "Outras"), na ordem do menu. */
  modules: { id: string; label: string }[];
}

/** Monta o índice com a mesma árvore da tela de edição (`buildPermissionTree`), para os números baterem. */
export function indexCatalog(catalog: Permission[]): RoleCatalogIndex {
  const tree: PermissionModuleNode[] = buildPermissionTree(catalog);
  const entries = new Map<string, CatalogEntry>();
  const modules: { id: string; label: string }[] = [];

  tree.forEach(module => {
    if (!module.isOther) modules.push({ id: module.id, label: module.label });
    module.children.forEach(child => {
      const screens = child.kind === 'area' ? child.screens : [child];
      screens.forEach(screen => {
        const base = {
          moduleId: module.isOther ? null : module.id,
          moduleLabel: module.label,
          itemLabel: child.label,
        };
        if (screen.menuAction) entries.set(screen.menuAction.value, { ...base, write: false });
        screen.readActions.forEach(action => entries.set(action.value, { ...base, write: false }));
        screen.writeActions.forEach(action => entries.set(action.value, { ...base, write: true }));
      });
    });
  });

  return { entries, modules };
}

/**
 * Leitura x gravação de uma claim fora do catálogo (legado, ou catálogo que não carregou): a mesma regra da tela de
 * edição (`classifyAction`) aplicada ao que vem depois do primeiro `_` (`LOANOPERATION_ATTACHMENT_LIST` →
 * `ATTACHMENT_LIST`). Claim de menu (`MENU...`) é leitura.
 */
export function classifyBySuffix(value: string): 'read' | 'write' {
  const upper = (value ?? '').toUpperCase();
  if (upper.startsWith('MENU')) return 'read';
  const separator = upper.indexOf('_');
  const code = separator >= 0 ? upper.substring(separator + 1) : upper;
  return classifyAction(code);
}

/** "a", "a e b", "a, b e c". */
export function joinWithAnd(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`;
}

/** Até quantos itens de um módulo a linha nomeia antes de "e mais N". */
const MAX_ITEMS_NAMED = 4;

/**
 * Os módulos do menu que o grupo libera, em uma linha:
 * - um módulo: "Controle · Endividamento, Derivativos e Gestão de riscos" (áreas ou telas soltas, até 4 e "mais N");
 * - vários módulos: os dois mais usados com os itens, "Controle (Endividamento e Derivativos) · Gerenciamento
 *   (Empresas) · mais 1 módulo";
 * - só o que está fora do menu: "Fora do menu · Dados econômicos".
 */
export function describeModules(permissions: string[], index: RoleCatalogIndex | null): string {
  if (!index) return '';
  const itemsByModule = new Map<string, string[]>();
  const outside: string[] = [];

  permissions.forEach(value => {
    const entry = index.entries.get(value);
    if (!entry) return;
    if (!entry.moduleId) {
      if (!outside.includes(entry.itemLabel)) outside.push(entry.itemLabel);
      return;
    }
    const items = itemsByModule.get(entry.moduleId) ?? [];
    if (!items.includes(entry.itemLabel)) items.push(entry.itemLabel);
    itemsByModule.set(entry.moduleId, items);
  });

  const touched = index.modules.filter(module => itemsByModule.has(module.id));
  if (!touched.length) {
    if (!outside.length) return 'Nenhum item do menu';
    return `Fora do menu · ${nameItems(outside)}`;
  }
  if (touched.length === 1) {
    const module = touched[0];
    return `${module.label} · ${nameItems(itemsByModule.get(module.id) ?? [])}`;
  }
  // Vários módulos: os que o grupo mais usa primeiro, cada um com os seus itens, até dois módulos. Só os nomes dos
  // módulos não diferenciava nada (quase todo grupo tinha "Gerenciamento, Controle, Performance, Negociação e Cog
  // IA"), e "Gerenciamento" num grupo de leitura do Controle sugeria acesso à gestão sem dizer que era um item só.
  const ranked = [...touched].sort(
    (a, b) => (itemsByModule.get(b.id)?.length ?? 0) - (itemsByModule.get(a.id)?.length ?? 0)
  );
  const shown = ranked
    .slice(0, MAX_MODULES_NAMED)
    .map(
      module =>
        `${module.label} (${nameItems(itemsByModule.get(module.id) ?? [], MAX_ITEMS_IN_MODULE)})`
    );
  const rest = ranked.length - shown.length;
  if (rest > 0) shown.push(rest === 1 ? 'mais 1 módulo' : `mais ${rest} módulos`);
  return shown.join(' · ');
}

/** Com vários módulos, quantos a linha detalha antes de "mais N módulos". */
const MAX_MODULES_NAMED = 2;
/** Com vários módulos, quantos itens de cada um a linha nomeia. */
const MAX_ITEMS_IN_MODULE = 2;

function nameItems(items: string[], max: number = MAX_ITEMS_NAMED): string {
  if (items.length <= max) return joinWithAnd(items);
  const rest = items.length - (max - 1);
  return `${items.slice(0, max - 1).join(', ')} e mais ${rest}`;
}

/** "40 leitura · nenhuma gravação", "7 leitura · 5 gravação", "nenhuma permissão". */
export function describeBreakdown(read: number, write: number): string {
  if (!read && !write) return 'nenhuma permissão';
  const writeText = write ? `${write} gravação` : 'nenhuma gravação';
  return `${read} leitura · ${writeText}`;
}

/** A linha da tabela de um grupo, com o índice do catálogo do papel dele (ou `null` se não carregou). */
export function buildRow(group: PermissionGroup, index: RoleCatalogIndex | null): GroupListRow {
  const permissions = [...new Set(group.permissions ?? [])];
  let write = 0;
  permissions.forEach(value => {
    const entry = index?.entries.get(value);
    const isWrite = entry ? entry.write : classifyBySuffix(value) === 'write';
    if (isWrite) write++;
  });
  const total = permissions.length;
  const read = total - write;
  const style = roleStyle(group.role?.name);

  return {
    group,
    roleId: group.roleId ?? group.role?.id ?? '',
    roleLabel: style.label,
    roleTone: style.tone,
    modulesText: describeModules(permissions, index),
    total,
    read,
    write,
    readPercent: total ? (read / total) * 100 : 0,
    writePercent: total ? (write / total) * 100 : 0,
    breakdown: describeBreakdown(read, write),
    usersCount: typeof group.usersCount === 'number' ? group.usersCount : null,
    isDefault: !!group.isDefault,
    searchKey: normalizeText(group.name),
  };
}

/** Ordem da tabela: tipo de usuário (ordem dos chips), depois "Padrão" primeiro, depois nome. */
export function sortRows(rows: GroupListRow[]): GroupListRow[] {
  return [...rows].sort((a, b) => {
    const byRole =
      roleStyle(a.group.role?.name).order - roleStyle(b.group.role?.name).order ||
      a.roleLabel.localeCompare(b.roleLabel, 'pt-BR');
    if (byRole) return byRole;
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return a.group.name.localeCompare(b.group.name, 'pt-BR');
  });
}

/** Chips "Todos" + um por tipo de usuário presente, com a contagem (a busca não muda a contagem). */
export function buildRoleChips(rows: GroupListRow[]): RoleChip[] {
  const byRole = new Map<string, RoleChip>();
  sortRows(rows).forEach(row => {
    const chip = byRole.get(row.roleId) ?? { roleId: row.roleId, label: row.roleLabel, count: 0 };
    chip.count++;
    byRole.set(row.roleId, chip);
  });
  return [{ roleId: '', label: 'Todos', count: rows.length }, ...byRole.values()];
}

/** Filtro por tipo (vazio = todos) e busca pelo nome, sem acento e sem caixa. */
export function filterRows(rows: GroupListRow[], roleId: string, query: string): GroupListRow[] {
  const term = normalizeText(query);
  return rows.filter(
    row => (!roleId || row.roleId === roleId) && (!term || row.searchKey.includes(term))
  );
}
